import React, { useEffect, useRef, useState } from 'react';
import styled from '@emotion/styled';
import { PICKER_COLOR } from '@/components/common/datePicker/pickerTokens';
import { AmPm, fromMinutes, toMinutes } from '@/components/common/datePicker/pickerUtils';

/**
 * 시간 휠(시안 altmock b) — 오전/오후 · 시(1~12) · 분(00~50, 10분) 세 기둥. 가운데 띠에 멈춘 칸이 선택.
 * 네이티브 select/time 없이 CSS scroll-snap 세로 목록으로 만든다 — 손가락 끌기·마우스 휠은 스크롤 그대로,
 * 마우스 끌기는 직접 옮기고, 위/아래 칸을 누르면 그쪽으로, 키보드 ↑↓·Home·End(기둥마다 Tab).
 * 고를 수 없는 값은 흐리게 보이고, 거기 멈추면 가장 가까운 가능한 값으로 되돌아간다.
 */

const ITEM_PX: number = 44;
const VISIBLE: number = 5;
/** 스크롤이 이만큼 멈추면 멈춘 칸을 고른다 */
const SETTLE_MS: number = 120;
const BAND_BG: string = '#EEF5F3';
const MINUTES: number[] = [0, 10, 20, 30, 40, 50];
const HOURS: number[] = Array.from({ length: 12 }, (_, i) => i + 1);
const AMPMS: AmPm[] = ['AM', 'PM'];

const Frame = styled.div`
  position: relative;
  display: grid;
  grid-template-columns: minmax(0, 1.2fr) minmax(0, 1fr) 14px minmax(0, 1fr);
  align-items: center;
  height: ${ITEM_PX * VISIBLE}px;

  /*
   * 작은 창 안 좁은 PC 팝업(FilterPanel data-popover-narrow, 2026-10-07 면접 요청 창) — 보이는 칸 5 → 3.
   * 다섯 칸이면 팝업이 창 아래 [닫기][보내기]까지 내려왔다. 위아래 여백도 한 칸씩이라 스크롤 계산(칸 높이)은 같다
   */
  [data-popover-narrow] & {
    height: ${ITEM_PX * 3}px;
  }

  /* 가운데 선택 띠 */
  &::before {
    content: '';
    position: absolute;
    left: 0;
    right: 0;
    top: ${ITEM_PX * 2}px;
    height: ${ITEM_PX}px;
    border-radius: 12px;
    background: ${BAND_BG};
    pointer-events: none;
  }

  [data-popover-narrow] &::before {
    top: ${ITEM_PX}px;
  }
`;

const Colon = styled.span`
  position: relative;
  text-align: center;
  font-size: 20px;
  font-weight: 800;
  color: ${PICKER_COLOR.text};
`;

const Column = styled.div`
  position: relative;
  height: ${ITEM_PX * VISIBLE}px;
  overflow-y: auto;
  overscroll-behavior: contain;
  scroll-snap-type: y mandatory;
  scrollbar-width: none;
  -webkit-overflow-scrolling: touch;
  touch-action: pan-y;
  outline: none;
  cursor: grab;
  user-select: none;
  /* 위아래 흐림 */
  mask-image: linear-gradient(to bottom, rgba(0, 0, 0, 0.15), #000 38%, #000 62%, rgba(0, 0, 0, 0.15));
  -webkit-mask-image: linear-gradient(to bottom, rgba(0, 0, 0, 0.15), #000 38%, #000 62%, rgba(0, 0, 0, 0.15));

  &::-webkit-scrollbar {
    display: none;
  }

  &:focus-visible {
    box-shadow: inset 0 0 0 2px ${PICKER_COLOR.primary};
    border-radius: 12px;
  }

  &::before,
  &::after {
    content: '';
    display: block;
    height: ${ITEM_PX * 2}px;
  }

  [data-popover-narrow] & {
    height: ${ITEM_PX * 3}px;
  }

  [data-popover-narrow] &::before,
  [data-popover-narrow] &::after {
    height: ${ITEM_PX}px;
  }
`;

const Item = styled.div<{ selected: boolean; off: boolean }>`
  height: ${ITEM_PX}px;
  display: flex;
  align-items: center;
  justify-content: center;
  scroll-snap-align: center;
  font-size: ${({ selected }) => (selected ? 21 : 18)}px;
  font-weight: ${({ selected }) => (selected ? 800 : 500)};
  color: ${({ selected, off }) => (off ? '#C7CFCC' : selected ? PICKER_COLOR.text : PICKER_COLOR.muted)};
  cursor: ${({ off }) => (off ? 'default' : 'pointer')};
`;

interface WheelItem {
  key: string;
  label: string;
  disabled: boolean;
}

interface WheelColumnProps {
  items: WheelItem[];
  selectedIndex: number;
  onSelect: (index: number) => void;
  /** 기둥 이름(오전 오후/시/분) */
  label: string;
  idPrefix: string;
}

/** 가장 가까운 고를 수 있는 칸 */
const nearestEnabled = (items: WheelItem[], index: number): number => {
  if (!items[index]?.disabled) return index;
  for (let d = 1; d < items.length; d += 1) {
    if (items[index + d] && !items[index + d].disabled) return index + d;
    if (items[index - d] && !items[index - d].disabled) return index - d;
  }
  return index;
};

const WheelColumn: React.FC<WheelColumnProps> = ({ items, selectedIndex, onSelect, label, idPrefix }) => {
  const ref = useRef<HTMLDivElement>(null);
  const timerRef = useRef<number | null>(null);
  const dragRef = useRef<{ y: number; top: number; moved: number } | null>(null);
  const mountedRef = useRef<boolean>(false);
  const itemsRef = useRef(items);
  itemsRef.current = items;
  const onSelectRef = useRef(onSelect);
  onSelectRef.current = onSelect;

  // 선택이 바뀌면(열 때·다른 기둥 때문에 옮겨질 때) 그 칸으로
  useEffect(() => {
    const box: HTMLDivElement | null = ref.current;
    if (!box) return;
    if (Math.round(box.scrollTop / ITEM_PX) === selectedIndex && mountedRef.current) return;
    box.scrollTo({ top: selectedIndex * ITEM_PX, behavior: mountedRef.current ? 'smooth' : 'auto' });
    mountedRef.current = true;
  }, [selectedIndex]);

  useEffect(() => () => {
    if (timerRef.current) window.clearTimeout(timerRef.current);
  }, []);

  // 마우스 휠 한 칸 = 한 칸. 그대로 두면 한 칸(약 100px)에 2~3칸씩 넘어간다.
  // 트랙패드처럼 잘게 오는 값은 모아서 한 칸 높이의 60%가 되면 한 칸 움직인다.
  const selectedRef = useRef<number>(selectedIndex);
  selectedRef.current = selectedIndex;
  const wheelAccRef = useRef<number>(0);
  useEffect(() => {
    const box: HTMLDivElement | null = ref.current;
    if (!box) return undefined;
    const onWheel = (event: WheelEvent): void => {
      event.preventDefault();
      const notch: boolean = event.deltaMode !== 0 || Math.abs(event.deltaY) >= 50;
      let dir: 1 | -1 | 0 = 0;
      if (notch) {
        dir = event.deltaY > 0 ? 1 : -1;
        wheelAccRef.current = 0;
      } else {
        wheelAccRef.current += event.deltaY;
        if (Math.abs(wheelAccRef.current) >= ITEM_PX * 0.6) {
          dir = wheelAccRef.current > 0 ? 1 : -1;
          wheelAccRef.current = 0;
        }
      }
      if (!dir) return;
      const list: WheelItem[] = itemsRef.current;
      let next: number = selectedRef.current;
      for (let i = selectedRef.current + dir; i >= 0 && i < list.length; i += dir) {
        if (!list[i].disabled) { next = i; break; }
      }
      if (next === selectedRef.current) return;
      selectedRef.current = next;
      box.scrollTo({ top: next * ITEM_PX, behavior: 'smooth' });
      onSelectRef.current(next);
    };
    // React 의 onWheel 은 passive 라 기본 스크롤을 막을 수 없다
    box.addEventListener('wheel', onWheel, { passive: false });
    return () => box.removeEventListener('wheel', onWheel);
  }, []);

  /** 멈춘 칸을 고른다 — 못 고르는 칸이면 가까운 가능한 칸으로 되돌린다 */
  const settle = (): void => {
    const box: HTMLDivElement | null = ref.current;
    if (!box || dragRef.current) return;
    const raw: number = Math.min(itemsRef.current.length - 1, Math.max(0, Math.round(box.scrollTop / ITEM_PX)));
    const index: number = nearestEnabled(itemsRef.current, raw);
    if (index !== raw || Math.abs(box.scrollTop - index * ITEM_PX) > 1) box.scrollTo({ top: index * ITEM_PX, behavior: 'smooth' });
    onSelectRef.current(index);
  };

  const onScroll = (): void => {
    if (timerRef.current) window.clearTimeout(timerRef.current);
    timerRef.current = window.setTimeout(settle, SETTLE_MS);
  };

  const go = (index: number): void => {
    const target: number = nearestEnabled(items, Math.min(items.length - 1, Math.max(0, index)));
    ref.current?.scrollTo({ top: target * ITEM_PX, behavior: 'smooth' });
    onSelect(target);
  };

  const step = (from: number, dir: 1 | -1): number => {
    for (let i = from + dir; i >= 0 && i < items.length; i += dir) if (!items[i].disabled) return i;
    return from;
  };

  // 마우스 끌기(손가락은 스크롤 그대로) — 끄는 동안 스냅을 끈다
  const onPointerDown = (event: React.PointerEvent<HTMLDivElement>): void => {
    if (event.pointerType !== 'mouse' || event.button !== 0 || !ref.current) return;
    dragRef.current = { y: event.clientY, top: ref.current.scrollTop, moved: 0 };
    ref.current.style.scrollSnapType = 'none';
    event.currentTarget.setPointerCapture?.(event.pointerId);
  };
  const onPointerMove = (event: React.PointerEvent<HTMLDivElement>): void => {
    const drag = dragRef.current;
    if (!drag || !ref.current) return;
    drag.moved = Math.max(drag.moved, Math.abs(event.clientY - drag.y));
    ref.current.scrollTop = drag.top - (event.clientY - drag.y);
  };
  const onPointerUp = (event: React.PointerEvent<HTMLDivElement>): void => {
    const drag = dragRef.current;
    if (!drag || !ref.current) return;
    dragRef.current = null;
    ref.current.style.scrollSnapType = '';
    if (drag.moved < 4) {
      // 누른 칸으로(위/아래 칸을 누르면 그쪽으로)
      const rect: DOMRect = ref.current.getBoundingClientRect();
      // 가운데 칸 기준 — 보이는 칸 수는 높이에서(좁은 팝업은 3칸)
      const offset: number = Math.floor((event.clientY - rect.top) / ITEM_PX) - Math.floor(Math.round(rect.height / ITEM_PX) / 2);
      go(selectedIndex + offset);
      return;
    }
    settle();
  };

  const onKeyDown = (event: React.KeyboardEvent<HTMLDivElement>): void => {
    let next: number | null = null;
    if (event.key === 'ArrowDown') next = step(selectedIndex, 1);
    else if (event.key === 'ArrowUp') next = step(selectedIndex, -1);
    else if (event.key === 'Home') next = nearestEnabled(items, 0);
    else if (event.key === 'End') next = nearestEnabled(items, items.length - 1);
    if (next == null) return;
    event.preventDefault();
    go(next);
  };

  return (
    <Column
      ref={ref}
      role="listbox"
      aria-label={label}
      tabIndex={0}
      aria-activedescendant={`${idPrefix}-${selectedIndex}`}
      onScroll={onScroll}
      onKeyDown={onKeyDown}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      onPointerCancel={onPointerUp}
    >
      {items.map((item, index) => (
        <Item
          key={item.key}
          id={`${idPrefix}-${index}`}
          role="option"
          aria-selected={index === selectedIndex}
          aria-disabled={item.disabled}
          selected={index === selectedIndex}
          off={item.disabled}
          // 손가락으로 누른 칸(마우스는 끌기 처리에서)
          onClick={(event: React.MouseEvent) => {
            if ((event.nativeEvent as PointerEvent).pointerType === 'mouse') return;
            go(index);
          }}
        >
          {item.label}
        </Item>
      ))}
    </Column>
  );
};

// ───────── 세 기둥 ─────────

const to24 = (ampm: AmPm, hour12: number): number => (hour12 % 12) + (ampm === 'PM' ? 12 : 0);
const compose = (ampm: AmPm, hour12: number, minute: number): string => fromMinutes(to24(ampm, hour12) * 60 + minute);

interface WheelState {
  ampm: AmPm;
  hour12: number;
  minute: number;
}

/** `HH:mm` → 휠 상태. 10분 단위가 아니면 아래 10분으로 */
const parse = (time: string): WheelState => {
  const minutes: number = toMinutes(time);
  const hour: number = Math.floor(minutes / 60);
  return { ampm: hour < 12 ? 'AM' : 'PM', hour12: hour % 12 === 0 ? 12 : hour % 12, minute: Math.floor((minutes % 60) / 10) * 10 };
};

export interface TimeWheelProps {
  /** `HH:mm` 또는 '' */
  value: string;
  /** 멈출 때마다 `HH:mm` */
  onChange: (value: string) => void;
  isDisabled: (time: string) => boolean;
  /** 값이 없을 때 처음 세울 시각(기본 오후 2:00) */
  fallback?: string;
  /** 스크린리더 이름 앞말(예: "시작 일시") */
  label: string;
}

const TimeWheel: React.FC<TimeWheelProps> = ({ value, onChange, isDisabled, fallback = '14:00', label }) => {
  const hourPossible = (ampm: AmPm, h: number): boolean => MINUTES.some((m) => !isDisabled(compose(ampm, h, m)));
  const ampmPossible = (ampm: AmPm): boolean => HOURS.some((h) => hourPossible(ampm, h));

  /** 못 고르는 조합이면 가장 가까운 가능한 값으로 */
  const normalize = (s: WheelState): WheelState => {
    let { ampm, hour12, minute } = s;
    // 그날 고를 수 있는 시각이 하나도 없으면 그대로 둔다(오전·오후를 번갈아 바꾸며 멈추지 않던 문제)
    if (!ampmPossible('AM') && !ampmPossible('PM')) return s;
    if (!ampmPossible(ampm)) ampm = ampm === 'AM' ? 'PM' : 'AM';
    if (!hourPossible(ampm, hour12)) {
      const order: number[] = [...HOURS].sort((a, b) => Math.abs(to24(ampm, a) - to24(ampm, hour12)) - Math.abs(to24(ampm, b) - to24(ampm, hour12)));
      hour12 = order.find((h) => hourPossible(ampm, h)) ?? hour12;
    }
    if (isDisabled(compose(ampm, hour12, minute))) {
      const order: number[] = [...MINUTES].sort((a, b) => Math.abs(a - minute) - Math.abs(b - minute));
      minute = order.find((m) => !isDisabled(compose(ampm, hour12, m))) ?? minute;
    }
    return { ampm, hour12, minute };
  };

  const [state, setState] = useState<WheelState>(() => normalize(parse(value || fallback)));
  const composed: string = compose(state.ampm, state.hour12, state.minute);
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;
  const idRef = useRef<string>(`wheel-${Math.random().toString(36).slice(2, 8)}`);

  // 부모가 값을 바꾸면 따라간다(회차 시작을 고르면 종료 +2시간 등)
  useEffect(() => {
    if (value && value !== composed) setState(normalize(parse(value)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  // 날짜·시작이 바뀌어 불가가 되면 가까운 값으로, 그리고 부모에 알린다
  useEffect(() => {
    const fixed: WheelState = normalize(state);
    if (fixed.ampm !== state.ampm || fixed.hour12 !== state.hour12 || fixed.minute !== state.minute) {
      setState(fixed);
      return;
    }
    if (composed !== value && !isDisabled(composed)) onChangeRef.current(composed);
  });

  const update = (patch: Partial<WheelState>): void => setState((prev) => normalize({ ...prev, ...patch }));

  return (
    <Frame data-no-swipe-back>
      <WheelColumn
        label={`${label} 오전 오후`}
        idPrefix={`${idRef.current}-ap`}
        items={AMPMS.map((ap) => ({ key: ap, label: ap === 'AM' ? '오전' : '오후', disabled: !ampmPossible(ap) }))}
        selectedIndex={AMPMS.indexOf(state.ampm)}
        onSelect={(index: number) => update({ ampm: AMPMS[index] })}
      />
      <WheelColumn
        label={`${label} 시`}
        idPrefix={`${idRef.current}-h`}
        items={HOURS.map((h) => ({ key: String(h), label: String(h), disabled: !hourPossible(state.ampm, h) }))}
        selectedIndex={HOURS.indexOf(state.hour12)}
        onSelect={(index: number) => update({ hour12: HOURS[index] })}
      />
      <Colon aria-hidden>:</Colon>
      <WheelColumn
        label={`${label} 분`}
        idPrefix={`${idRef.current}-m`}
        items={MINUTES.map((m) => ({ key: String(m), label: String(m).padStart(2, '0'), disabled: isDisabled(compose(state.ampm, state.hour12, m)) }))}
        selectedIndex={MINUTES.indexOf(state.minute)}
        onSelect={(index: number) => update({ minute: MINUTES[index] })}
      />
    </Frame>
  );
};

export default TimeWheel;
